import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  Alert,
  Button,
  Card,
  Dialog,
  EmptyState,
  SectionHeader,
  Select,
  StatusChip,
  Table,
  Tabs,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
} from "@/components/ui";
import { apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { createInsightsApi, type InsightRange } from "@/modules/dashboard/insights/api";
import {
  BarChart,
  CHART_COLORS,
  Delta,
  HBars,
  Kpi,
  KpiGrid,
  Legend,
  Meter,
  Note,
  Stat,
  StatGrid,
  TwoCol,
} from "@/modules/dashboard/insights/charts";
import {
  formatHours,
  formatLabel,
  formatMinutes,
  formatMoney,
  formatNumber,
  formatPercent,
  formatPercentPoints,
} from "@/modules/dashboard/insights/format";
import { InsightBoundary, RangeSelect } from "@/modules/dashboard/insights/parts";
import type { MarketplaceDto } from "@/modules/dashboard/insights/types";
import { useInsight } from "@/modules/dashboard/insights/useInsight";
import { createMarketplaceApi } from "../api";
import { buildAttention, NOT_MEASURABLE_YET, type AttentionItem } from "../attention";
import { MarketplaceSignalsPanel } from "./MarketplaceQueuePages";

const SEVERITIES = [
  { value: "low", label: "Low" },
  { value: "normal", label: "Normal" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
  { value: "critical", label: "Critical" },
];

/**
 * Marketplace Health: are the courts on HUDDL and the players who want them
 * meeting each other? Supply, demand, conversion, reliability and utilisation,
 * measured over a range, plus the signals and opportunities staff log about it.
 */
export function MarketplaceHubPage() {
  const client = useApiClient();
  const api = useMemo(() => createInsightsApi(client), [client]);
  const [days, setDays] = useState<InsightRange>(30);
  const [tab, setTab] = useState("health");

  usePageBreadcrumbs([{ label: "HUDDL HQ", to: "/app" }, { label: "Marketplace Health" }]);

  const state = useInsight(() => api.marketplace(days), [api, days]);

  return (
    <Stack gap={24}>
      <SectionHeader
        title="Marketplace Health"
        description="Are the courts on HUDDL and the players who want them finding each other? Supply, demand, checkout, cancellations and how full the courts are."
        actions={
          tab === "health" ? (
            <Flex gap={12} align="flex-end" wrap>
              <RangeSelect value={days} onChange={setDays} id="marketplace-range" />
              <Button variant="secondary" onClick={state.reload} loading={state.loading}>
                Refresh
              </Button>
            </Flex>
          ) : null
        }
      />

      <Tabs
        value={tab}
        onValueChange={setTab}
        items={[
          {
            id: "health",
            label: "Health",
            content: (
              <InsightBoundary state={state} what="marketplace health">
                {(m) => <HealthTab m={m} days={days} onLogged={state.reload} />}
              </InsightBoundary>
            ),
          },
          { id: "signals", label: "Signals and opportunities", content: <MarketplaceSignalsPanel /> },
        ]}
      />
    </Stack>
  );
}

// ---------------------------------------------------------------------------

function HealthTab({ m, days, onLogged }: { m: MarketplaceDto; days: number; onLogged: () => void }) {
  const navigate = useNavigate();
  const attention = useMemo(() => buildAttention(m), [m]);
  const [logging, setLogging] = useState<AttentionItem | null>(null);

  const noData =
    m.demand.bookings === 0 && m.supply.active_offers === 0 && m.supply.live_businesses === 0;
  const holdsDecided = m.holds.converted + m.holds.expired + m.holds.cancelled;
  const cancelled = m.cancelled_by;
  const lead = m.cancellation_lead;
  const util = m.utilisation;

  return (
    <Stack gap={24}>
      {noData ? (
        <Card>
          <EmptyState
            title="The marketplace is empty"
            description="This fills in when a business registers and is approved, lists a court with a price and opening hours, and a player books it. Nothing needs to be configured here."
            reason="none"
          />
        </Card>
      ) : null}

      <KpiGrid>
        <Kpi
          label={`Bookings (${days} days)`}
          value={formatNumber(m.demand.bookings)}
          meta={<Delta current={m.demand.bookings} previous={m.demand.bookings_prev} label={`previous ${days} days`} />}
        />
        <Kpi
          label="Customers who booked"
          value={formatNumber(m.demand.customers)}
          meta={`${formatNumber(m.demand.new_customers)} first-time · ${formatNumber(m.demand.returning_customers)} returning`}
        />
        <Kpi
          label="Checkout conversion"
          value={formatPercentPoints(m.holds.conversion_rate, 0)}
          meta={
            holdsDecided === 0
              ? "No finished checkouts in this range"
              : `${formatNumber(m.holds.converted)} of ${formatNumber(holdsDecided)} finished checkouts became bookings`
          }
        />
        <Kpi
          label="Cancellation rate"
          value={formatPercentPoints(m.reliability.cancellation_rate, 0)}
          meta={`${formatNumber(m.reliability.cancelled)} of ${formatNumber(m.reliability.considered)} bookings`}
          attention={(m.reliability.cancellation_rate ?? 0) >= 30 && m.reliability.considered >= 5}
        />
        <Kpi
          label="Court utilisation, past"
          value={formatPercent(util.trailing.rate)}
          meta={
            util.trailing.available_hours > 0
              ? `${util.trailing.booked_hours.toFixed(1)} of ${util.trailing.available_hours.toFixed(0)} open hours in ${days} days`
              : "No open hours in this range"
          }
        />
        <Kpi
          label={`Court utilisation, next ${util.forward_days} days`}
          value={formatPercent(util.forward.rate)}
          meta={
            util.forward.available_hours > 0
              ? `${util.forward.booked_hours.toFixed(1)} of ${util.forward.available_hours.toFixed(0)} open hours already booked`
              : "No open hours ahead"
          }
        />
        <Kpi
          label="Bookable listings"
          value={formatNumber(m.supply.sellable_offers)}
          meta={`${formatNumber(m.supply.live_businesses)} live businesses · ${formatNumber(m.supply.active_offers)} active listings`}
          onClick={() => navigate("/app/businesses")}
        />
      </KpiGrid>

      <Card
        header={
          <Flex justify="space-between" align="center" wrap>
            <Text role="title">Needs a look</Text>
            <Text role="small" color="secondary">
              {attention.length === 0 ? "Nothing flagged" : `${attention.length} item${attention.length === 1 ? "" : "s"}`}
            </Text>
          </Flex>
        }
      >
        {attention.length === 0 ? (
          <EmptyState
            title="Nothing needs attention"
            description="The checks look at missing hours or prices, courts with no listing, high cancellation, low checkout conversion and interest with nothing to book. None apply to the current data."
            reason="complete"
          />
        ) : (
          <Stack gap={12}>
            {attention.map((item) => (
              <Flex key={item.id} gap={12} align="flex-start" justify="space-between" wrap>
                <Stack gap={4} style={{ flex: "1 1 320px", minWidth: 0 }}>
                  <Flex gap={8} align="center" wrap>
                    <StatusChip tone={item.tone === "danger" ? "danger" : item.tone === "warning" ? "warning" : "info"}>
                      {item.tone === "danger" ? "Problem" : item.tone === "warning" ? "Check" : "Note"}
                    </StatusChip>
                    <Text role="bodyStrong">{item.title}</Text>
                  </Flex>
                  <Text role="small" color="secondary">
                    {item.detail}
                  </Text>
                </Stack>
                <Flex gap={8}>
                  {item.href ? (
                    <Button variant="ghost" size="sm" onClick={() => navigate(item.href!)}>
                      Open
                    </Button>
                  ) : null}
                  {item.signal ? (
                    <Button variant="secondary" size="sm" onClick={() => setLogging(item)}>
                      Log as signal
                    </Button>
                  ) : null}
                </Flex>
              </Flex>
            ))}
          </Stack>
        )}
      </Card>

      <TwoCol>
        <Card header={<Text role="title">Bookings and cancellations</Text>}>
          <Stack gap={8}>
            <BarChart
              labels={m.series.map((p) => p.date)}
              series={[
                { key: "bookings", label: "Bookings", color: CHART_COLORS.primary, values: m.series.map((p) => p.bookings) },
                { key: "cancelled", label: "Cancelled", color: CHART_COLORS.danger, values: m.series.map((p) => p.cancelled) },
              ]}
              ariaLabel={`Bookings created and cancelled per day over the last ${days} days`}
              emptyText="No bookings in this range"
            />
            <Legend
              series={[
                { key: "bookings", label: "Bookings", color: CHART_COLORS.primary },
                { key: "cancelled", label: "Cancelled", color: CHART_COLORS.danger },
              ]}
            />
          </Stack>
        </Card>

        <Card header={<Text role="title">What happens at checkout</Text>}>
          <Stack gap={12}>
            {m.holds.started === 0 ? (
              <Note>No court was held in this range. A hold starts when a player begins checkout.</Note>
            ) : (
              <HBars
                items={[
                  { label: "Started checkout", value: m.holds.started },
                  { label: "Became a booking", value: m.holds.converted },
                  { label: "Expired unpaid", value: m.holds.expired },
                  { label: "Cancelled or released", value: m.holds.cancelled },
                  { label: "Still open", value: m.holds.in_progress },
                ]}
              />
            )}
            <Note>
              Conversion counts only checkouts that have finished, so a hold still open does not count against it.
            </Note>
          </Stack>
        </Card>
      </TwoCol>

      <Card header={<Text role="title">Supply and demand by sport</Text>}>
        {m.sports.length === 0 ? (
          <Note>
            No sport has any interest, listing or booking yet. Members choose sports in onboarding; businesses attach a
            sport to each listing.
          </Note>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <Table>
              <Thead>
                <Tr>
                  <Th>Sport</Th>
                  <Th>Members interested</Th>
                  <Th>Active listings</Th>
                  <Th>Businesses</Th>
                  <Th>Bookings ({days} d)</Th>
                  <Th>Booked value</Th>
                  <Th>Balance</Th>
                </Tr>
              </Thead>
              <Tbody>
                {m.sports.map((s) => (
                  <Tr key={s.sport}>
                    <Td>{s.sport}</Td>
                    <Td>{formatNumber(s.members_interested)}</Td>
                    <Td>{formatNumber(s.active_offers)}</Td>
                    <Td>{formatNumber(s.businesses)}</Td>
                    <Td>{formatNumber(s.bookings)}</Td>
                    <Td>{formatMoney(s.booked_value, "PHP")}</Td>
                    <Td>
                      {s.members_interested > 0 && s.active_offers === 0 ? (
                        <StatusChip tone="warning">Interest, no supply</StatusChip>
                      ) : s.active_offers > 0 && s.members_interested === 0 && s.bookings === 0 ? (
                        <StatusChip tone="info">Supply, no interest yet</StatusChip>
                      ) : (
                        <StatusChip tone="success">Both sides present</StatusChip>
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </div>
        )}
        <Note>
          Interest is the sports members chose on their profile. Supply is active listings. Demand is bookings created in
          the range, valued at the booking's price snapshot in PHP.
        </Note>
      </Card>

      <TwoCol>
        <Card header={<Text role="title">Who cancels, and when</Text>}>
          <Stack gap={16}>
            {m.reliability.cancelled === 0 ? (
              <Note>No booking was cancelled in this range.</Note>
            ) : (
              <>
                <Stack gap={8}>
                  <Text role="label" color="secondary">
                    Cancelled by
                  </Text>
                  <HBars
                    items={[
                      { label: "The player", value: cancelled.customer },
                      { label: "The business or staff", value: cancelled.business_or_staff },
                      { label: "System or not recorded", value: cancelled.system_or_unattributed },
                    ]}
                  />
                </Stack>
                <Stack gap={8}>
                  <Text role="label" color="secondary">
                    How long before the start time
                  </Text>
                  <HBars
                    items={[
                      { label: "After the start", value: lead.after_start },
                      { label: "Under 2 hours", value: lead.under_2h },
                      { label: "2 to 24 hours", value: lead.h2_to_24 },
                      { label: "1 to 3 days", value: lead.d1_to_3 },
                      { label: "More than 3 days", value: lead.over_3d },
                    ]}
                  />
                </Stack>
              </>
            )}
            <StatGrid>
              <Stat label="Completed" value={formatNumber(m.reliability.completed)} />
              <Stat label="Refunded" value={formatNumber(m.reliability.refunded)} />
              <Stat label="Disputed" value={formatNumber(m.reliability.disputed)} />
              <Stat label="Failed to complete" value={formatNumber(m.reliability.failed)} />
            </StatGrid>
          </Stack>
        </Card>

        <Card header={<Text role="title">Speed</Text>}>
          <Stack gap={12}>
            <StatGrid>
              <Stat
                label="Median time for a business to confirm a request"
                value={formatMinutes(m.confirmation_response.median_minutes)}
                note={
                  m.confirmation_response.samples === 0
                    ? "No booking has needed confirmation yet"
                    : `From ${formatNumber(m.confirmation_response.samples)} confirmed booking${m.confirmation_response.samples === 1 ? "" : "s"}`
                }
              />
              <Stat
                label="Median time from a business being created to its first booking"
                value={formatHours(m.business_time_to_first_booking.median_hours)}
                note={`${formatNumber(m.business_time_to_first_booking.businesses_with_booking)} of ${formatNumber(m.business_time_to_first_booking.live_businesses)} active businesses have had a booking`}
              />
            </StatGrid>
            <Note>
              Time to first booking runs from when the business record was created, which includes review time before it
              was approved.
            </Note>
          </Stack>
        </Card>
      </TwoCol>

      <Card header={<Text role="title">Court utilisation</Text>}>
        {util.resources.length === 0 ? (
          <Note>No court belongs to an active business yet.</Note>
        ) : (
          <Stack gap={8}>
            <div style={{ overflowX: "auto" }}>
              <Table>
                <Thead>
                  <Tr>
                    <Th>Court</Th>
                    <Th>Business</Th>
                    <Th>Past {days} days</Th>
                    <Th>Next {util.forward_days} days</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {util.resources.map((r) => (
                    <Tr
                      key={r.resource_id}
                      onClick={() => navigate(`/app/businesses/resources/${r.resource_id}`)}
                      style={{ cursor: "pointer" }}
                    >
                      <Td>{r.resource}</Td>
                      <Td>{r.business}</Td>
                      <Td>
                        {r.has_hours ? (
                          <UtilCell block={r.trailing} />
                        ) : (
                          <StatusChip tone="warning">No opening hours</StatusChip>
                        )}
                      </Td>
                      <Td>{r.has_hours ? <UtilCell block={r.forward} /> : "—"}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </div>
            <Note>
              Utilisation is booked hours divided by open hours, using the same weekly hours and blackouts the booking page
              uses. A court open 16 hours a day and booked 4 is 25%. Bookings outside declared hours do not count.
              {util.bookings_without_court > 0
                ? ` ${util.bookings_without_court} booking${util.bookings_without_court === 1 ? " is" : "s are"} not tied to a court and left out.`
                : ""}
            </Note>
          </Stack>
        )}
      </Card>

      <Card header={<Text role="title">Businesses by demand ({days} days)</Text>}>
        {m.top_businesses.length === 0 ? (
          <Note>No active business yet.</Note>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <Table>
              <Thead>
                <Tr>
                  <Th>Business</Th>
                  <Th>Bookings</Th>
                  <Th>Cancelled</Th>
                  <Th>Cancellation rate</Th>
                  <Th>Booked value</Th>
                </Tr>
              </Thead>
              <Tbody>
                {m.top_businesses.map((b) => (
                  <Tr key={b.business_id} onClick={() => navigate(`/app/businesses/${b.business_id}`)} style={{ cursor: "pointer" }}>
                    <Td>{b.name}</Td>
                    <Td>{formatNumber(b.bookings)}</Td>
                    <Td>{formatNumber(b.cancelled)}</Td>
                    <Td>{formatPercentPoints(b.cancellation_rate, 0)}</Td>
                    <Td>{formatMoney(b.booked_value, "PHP")}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </div>
        )}
      </Card>

      <TwoCol>
        <Card header={<Text role="title">Supply by status</Text>}>
          <Stack gap={12}>
            <Text role="label" color="secondary">
              Businesses
            </Text>
            <HBars
              empty="No business yet."
              items={Object.entries(m.supply.businesses_by_status).map(([k, v]) => ({ label: formatLabel(k), value: v }))}
            />
            <Text role="label" color="secondary">
              Courts
            </Text>
            <HBars
              empty="No court yet."
              items={Object.entries(m.supply.resources_by_status).map(([k, v]) => ({ label: formatLabel(k), value: v }))}
            />
            <Text role="label" color="secondary">
              Listings
            </Text>
            <HBars
              empty="No listing yet."
              items={Object.entries(m.supply.offers_by_status).map(([k, v]) => ({ label: formatLabel(k), value: v }))}
            />
          </Stack>
        </Card>

        <Card header={<Text role="title">Not measurable yet</Text>}>
          <Stack gap={12}>
            <Note>
              A real operator would want these, but HUDDL does not record what they need. Each says what is missing.
            </Note>
            {NOT_MEASURABLE_YET.map((item) => (
              <Stack key={item.title} gap={4}>
                <Text role="bodyStrong">{item.title}</Text>
                <Text role="small" color="secondary">
                  {item.why}
                </Text>
              </Stack>
            ))}
          </Stack>
        </Card>
      </TwoCol>

      <LogSignalDialog item={logging} onClose={() => setLogging(null)} onLogged={onLogged} />
    </Stack>
  );
}

function UtilCell({ block }: { block: { available_hours: number; booked_hours: number; rate: number | null } }) {
  if (block.rate === null) return <Text role="small" color="secondary">No open hours in range</Text>;
  return (
    <Stack gap={4}>
      <Text role="small">
        {formatPercent(block.rate)} · {block.booked_hours.toFixed(1)} of {block.available_hours.toFixed(0)} h
      </Text>
      <Meter fraction={block.rate} label={`${formatPercent(block.rate)} utilised`} />
    </Stack>
  );
}

function LogSignalDialog({
  item,
  onClose,
  onLogged,
}: {
  item: AttentionItem | null;
  onClose: () => void;
  onLogged: () => void;
}) {
  const client = useApiClient();
  const marketplaceApi = useMemo(() => createMarketplaceApi(client), [client]);
  const toast = useToast();
  const [severity, setSeverity] = useState("normal");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!item?.signal) return;
    setSaving(true);
    setError(null);
    try {
      await marketplaceApi.createSignal({ ...item.signal, severity });
      toast.push({
        title: "Signal logged",
        description: "It now appears under Signals and opportunities.",
        tone: "success",
      });
      onClose();
      onLogged();
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={item !== null}
      onClose={saving ? () => undefined : onClose}
      title="Log as a Marketplace Health signal"
      actions={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} loading={saving}>
            Log signal
          </Button>
        </>
      }
    >
      <Stack gap={12}>
        <Text role="bodyStrong">{item?.title}</Text>
        <Text role="small" color="secondary">
          {item?.detail}
        </Text>
        <Text role="small" color="secondary">
          This records the finding so someone can own and resolve it. It does not change the court, business or booking.
          Logging the same finding twice creates two signals.
        </Text>
        <Select
          label="Severity"
          value={severity}
          onChange={(e) => setSeverity(e.target.value)}
          options={SEVERITIES}
        />
        {error ? (
          <Alert tone="danger" title="Could not log the signal">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}
